import('./bootstrap');

import { createApp, h } from 'vue';
import { createInertiaApp, router } from '@inertiajs/vue3';
import VueCookies from 'vue3-cookies'
import VueGtag from "vue-gtag";
import Toast, { useToast } from "vue-toastification";
import { VueReCaptcha } from "vue-recaptcha-v3";
import "vue-toastification/dist/index.css";
import { i18nVue } from 'laravel-vue-i18n';
import {resolvePageComponent} from "laravel-vite-plugin/inertia-helpers";
import * as Sentry from "@sentry/vue";
import { createHead } from '@vueuse/head';
import vSafeHtml from '@/Directives/vSafeHtml';


const appName = window.document.getElementsByTagName('title')[0]?.innerText || 'Laravel'

createInertiaApp({
    progress: {
        color: '#4B5563',
    },
    title: title => title ? `${title} - ${appName}` : appName,
    resolve: async (name) => {
        let componentPath = `./Pages/${name}`
            // If name starts with ./theme then it means we have the full component path in the name itself
        if (name.startsWith('./theme')) {
            componentPath = name
        }
        if (! componentPath.endsWith('.vue')) {
            componentPath = `${componentPath}.vue`;
        }

        return resolvePageComponent(`${componentPath}`, import.meta.glob('./Pages/**/*.vue'))
    },
    setup({ el, App, props, plugin }) {
        const head = createHead();
        const app = createApp({ render: () => h(App, props) })
            .use(plugin)
            .use(head)
            .use(i18nVue, {
                resolve: lang => {
                    const langs = import.meta.glob('../../lang/*.json', { eager: true });
                    return langs[`../../lang/${lang}.json`].default;
                }
            })
            .use(VueCookies)
            .use(Toast)
            .use(VueGtag, {
                config: { id: props.initialPage.props.gtag }
            })
            .use(VueReCaptcha, {
                siteKey: import.meta.env.VITE_RECAPTCHA_SITE_KEY,
                loaderOptions: {
                    useRecaptchaNet: true,
                    autoHideBadge: true
                }
            });

        // Initial Sentry only in non-local environments
        if (import.meta.env.VITE_APP_ENV === 'production') {
            Sentry.init({
                app,
                dsn: import.meta.env.VITE_SENTRY_DSN_PUBLIC,
                release: props.initialPage.props.release,
                trackComponents: true,
                environment: import.meta.env.VITE_APP_ENV,
                integrations: [
                    new Sentry.BrowserTracing({
                        // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
                        tracePropagationTargets: ["localhost", /^https:\/\/deeprootsbible\.com\//]

                    }),
                    new Sentry.Replay(),
                ],
                // Performance Monitoring
                tracesSampleRate: 0, // Use lower value on production. 1.0 is max.
                // Session Replay
                replaysSessionSampleRate: 0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
                replaysOnErrorSampleRate: 0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
                ignoreErrors:[
                    "Non-Error exception captured",
                    "Non-Error promise rejection captured"
                ],
            });
        }

        // Handle unhandled promise rejections for axios network errors
        window.addEventListener('unhandledrejection', (event) => {
            if (event.reason && event.reason.isAxiosError && event.reason.code === 'ERR_NETWORK') {
                console.warn('Axios network error occurred:', event.reason.message);
                event.preventDefault(); // Prevent the error from being logged as unhandled
            }
        });

        app.directive('safe-html', vSafeHtml);
        app.mixin({ methods: { route } })
            .mount(el);

        // Global handler: intercept HTTP 429 (rate-limited upload) responses and
        // display a user-friendly toast instead of Inertia's default iframe fallback.
        router.on('invalid', async (event) => {
            const response = event?.detail?.response;

            if (response?.status === 429) {
                event.preventDefault();

                let body = null;

                if (typeof response.json === 'function') {
                    body = await response.json().catch(() => null);
                } else if (response.data && typeof response.data === 'object') {
                    body = response.data;
                } else if (typeof response.responseText === 'string') {
                    try {
                        body = JSON.parse(response.responseText || '{}');
                    } catch (e) {
                        body = null;
                    }
                }

                const toast = useToast();
                toast.error(body?.message ?? 'Too many file upload attempts. Please wait before trying again.');
            }
        });
    },
});
